/**
 * I-3 Coaching — Journal posts (fallback when CMS has no published entries).
 * Optional fields power filters, featured layout, and read-time on the index.
 */

import { resolveBlogFormat } from '../cms/blogFormat';

export type I3BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  /** Display date (YYYY-MM-DD) */
  publishedAt: string;
  /** daily = short reflection; devotional = longer teaching; article = news / updates */
  format?: 'daily' | 'devotional' | 'article';
  /** Topic label for chips / filtering */
  topic?: string;
  /** If true, eligible for the large “featured” slot on the journal index */
  featured?: boolean;
  /** Optional; otherwise estimated from body length */
  readTimeMinutes?: number;
  heroImage?: string;
  /** Main pull quote (editorial “Transparent Tuesday” style) */
  quote?: string;
  /** Follow-up line labeled “Meaning:” on the article page */
  meaning?: string;
  paragraphs: string[];
};

/** Pull-quote reflections with no Meaning block or body — journal only, no article page. */
export function isI3BlogQuoteOnly(post: Pick<I3BlogPost, 'quote' | 'meaning' | 'paragraphs'>): boolean {
  const hasQuote = !!post.quote?.trim();
  const hasMeaning = !!post.meaning?.trim();
  const hasBody = post.paragraphs.some((p) => p.trim());
  return hasQuote && !hasMeaning && !hasBody;
}

export function estimateReadMinutes(post: I3BlogPost): number {
  if (post.readTimeMinutes != null && post.readTimeMinutes > 0) return post.readTimeMinutes;
  const extra = [post.quote, post.meaning].filter(Boolean).join(' ');
  const words = `${post.paragraphs.join(' ')} ${extra}`.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export const I3_BLOG_POSTS: I3BlogPost[] = [
  {
    slug: 'sunday-shout-2026-07-26',
    title: 'Sunday Shout',
    excerpt:
      'God’s unchanging character is so constant, so concrete, so consistent; it turns whispers into cheers and ordinary days into celebrations.',
    author: 'Troy Smith',
    publishedAt: '2026-07-26',
    format: 'daily',
    topic: 'Sunday Shout',
    readTimeMinutes: 2,
    quote:
      'God’s unchanging character is so constant, so concrete, so consistent; it turns whispers into cheers and ordinary days into celebrations.',
    paragraphs: [],
  },
  {
    slug: 'standing-on-business-saturday-2026-07-25',
    title: 'Standing on Business Saturday',
    excerpt:
      'Standing on business is more than bold talk; it\'s an unshakable foundation forged by fierce faith and fixed principles.',
    author: 'Troy Smith',
    publishedAt: '2026-07-25',
    format: 'daily',
    topic: 'Standing on Business Saturday',
    readTimeMinutes: 2,
    quote:
      'Standing on business is more than bold talk; it\'s an unshakable foundation forged by fierce faith and fixed principles.',
    paragraphs: [],
  },
  {
    slug: 'stick-to-the-script-2026-07-24',
    title: 'Stick To The Script!',
    excerpt:
      '1 Corinthians 1:23 — When life gets loud and lanes get blurry, sticking to the script means choosing Christ over confusion, conviction over convenience, and calling over crowd approval.',
    author: 'Troy Smith',
    publishedAt: '2026-07-24',
    format: 'devotional',
    topic: 'Faith & purpose',
    readTimeMinutes: 5,
    quote:
      'When life tries to remix your story, please remember God already wrote the masterpiece. Stick to the Script!',
    meaning:
      'The cross is our compass — a sacred storyline that silences shame, steadies the soul, and sets us back on course.',
    paragraphs: [
      'When life gets loud and lanes get blurry, “Sticking To The Script” means choosing Christ over confusion, conviction over convenience, and calling over crowd approval. The cross is our compass; a sacred storyline that silences shame, steadies the soul, and sets us back on course.',
      'Let your faith be consistent, courageous, and cross-centered. When pressure pushes you to perform, pivot back to the plot God already penned. When temptation tries to twist your testimony, trust the truth that transformed you.',
      'In the NFL, even the best teams get punched in the mouth. The scoreboard might slip, momentum might swing, and the crowd might roar against them. But championship teams don’t panic — they stick to the gameplan. Coaches remind players, “Trust the playbook. Run what we practiced.” Likewise, when life blitzes you, when the enemy seems to be advancing, when circumstances look like you’re losing, don’t ditch God’s “script.” The cross is your playbook. Christ is your Coach. The Gospel is your gameplan. Stick to it, even when the pressure is pushing you to improvise.',
      'The world may call the cross foolish, but for us, it’s fuel, foundation, and freedom. Stay faithful to what God already said — His script still stands, still speaks, still saves.',
      'Introspection: Where am I tempted to rewrite what God has already made clear? What part of my walk needs to become more cross-centered, consistent, and courageous?',
    ],
  },
  {
    slug: 'freedom-friday-2026-07-24',
    title: 'Freedom Friday',
    excerpt:
      'Freedom isn\'t a chant from the crowd, it\'s a compass within; mantras make noise, mindsets make moves.',
    author: 'Troy Smith',
    publishedAt: '2026-07-24',
    format: 'daily',
    topic: 'Freedom Friday',
    readTimeMinutes: 2,
    quote:
      'Freedom isn\'t a chant from the crowd, it\'s a compass within; mantras make noise, mindsets make moves.',
    paragraphs: [],
  },
  {
    slug: 'transformation-thursday-2026-07-23',
    title: 'Transformation Thursday',
    excerpt:
      'Healing sets you free, but it also sets a standard, because your courage to overcome becomes permission for someone else to begin.',
    author: 'Troy Smith',
    publishedAt: '2026-07-23',
    format: 'daily',
    topic: 'Transformation Thursday',
    readTimeMinutes: 2,
    quote:
      'Healing sets you free, but it also sets a standard, because your courage to overcome becomes permission for someone else to begin.',
    paragraphs: [],
  },
  {
    slug: 'wisdom-wednesday-2026-07-22',
    title: 'Wisdom Wednesday',
    excerpt:
      'Wisdom is the ripe fruit of relentless reflection and real-world reps; introspection plants the seed, application grows the tree.',
    author: 'Troy Smith',
    publishedAt: '2026-07-22',
    format: 'daily',
    topic: 'Wisdom Wednesday',
    readTimeMinutes: 2,
    quote:
      'Wisdom is the ripe fruit of relentless reflection and real-world reps; introspection plants the seed, application grows the tree.',
    paragraphs: [],
  },
  {
    slug: 'thankful-tuesday-2026-07-21',
    title: 'Thankful Tuesday',
    excerpt:
      'Gratitude ain\'t for sale and can\'t be schooled into the spirit; it\'s a seed you seek and grow into gold.',
    author: 'Troy Smith',
    publishedAt: '2026-07-21',
    format: 'daily',
    topic: 'Thankful Tuesday',
    readTimeMinutes: 2,
    quote:
      'Gratitude ain\'t for sale and can\'t be schooled into the spirit; it\'s a seed you seek and grow into gold.',
    paragraphs: [],
  },
  {
    slug: 'mindset-monday-2026-07-20',
    title: 'Mindset Monday',
    excerpt:
      'Longevity without legacy is borrowed time on a boarded-up boulevard. Let your impact echo louder than your existence.',
    author: 'Troy Smith',
    publishedAt: '2026-07-20',
    format: 'daily',
    topic: 'Mindset Monday',
    readTimeMinutes: 2,
    quote:
      'Longevity without legacy is borrowed time on a boarded-up boulevard. Let your impact echo louder than your existence.',
    paragraphs: [],
  },
  {
    slug: 'sovereignty-sunday-2026-06-21',
    title: 'Sovereignty Sunday',
    excerpt:
      'Surrendering to God\'s sovereign script may seem strange in the present, but the promised prize proves the process is purposeful.',
    author: 'Troy Smith',
    publishedAt: '2026-06-21',
    format: 'daily',
    topic: 'Sovereignty Sunday',
    readTimeMinutes: 2,
    quote:
      'Surrendering to God\'s sovereign script may seem strange in the present, but the promised prize proves the process is purposeful. Faith-filled focus finds fulfillment—what feels unclear now becomes unmistakably worth it.',
    paragraphs: [],
  },
  {
    slug: 'steadfast-saturday-2026-06-20',
    title: 'Steadfast Saturday',
    excerpt:
      'Resilient rhythms ripple like a rock\'s reverberation when entering a pond. Steadily strengthening spirits and sparking lasting change that a surrender would have silenced.',
    author: 'Troy Smith',
    publishedAt: '2026-06-20',
    format: 'daily',
    topic: 'Steadfast Saturday',
    readTimeMinutes: 2,
    quote:
      'Resilient rhythms ripple like a rock\'s reverberation when entering a pond. Steadily strengthening spirits and sparking lasting change that a surrender would have silenced. Leaning in, builds bold breakthroughs no quick quit could ever create.',
    paragraphs: [],
  },
  {
    slug: 'focused-friday-2026-06-19',
    title: 'Focused Friday',
    excerpt:
      'Focused living calls for brave boundaries—cultivating the courage to cut the cord of certain people, places, and patterns to protect your purpose and peace.',
    author: 'Troy Smith',
    publishedAt: '2026-06-19',
    format: 'daily',
    topic: 'Focused Friday',
    readTimeMinutes: 2,
    quote:
      'Focused living calls for brave boundaries—cultivating the courage to cut the cord of certain people, places, and patterns to protect your purpose and peace. Locking in means letting go, so your energy aligns with what truly elevates and endures.',
    paragraphs: [],
  },
  {
    slug: 'triumphant-thursday-2026-06-18',
    title: 'Triumphant Thursday',
    excerpt:
      'We were not created to be confined—mentally, emotionally, or physically—but to live with bold belief, brave boundaries, and boundless becoming.',
    author: 'Troy Smith',
    publishedAt: '2026-06-18',
    format: 'daily',
    topic: 'Triumphant Thursday',
    readTimeMinutes: 2,
    quote:
      'We were not created to be confined—mentally, emotionally, or physically—but to live with bold belief, brave boundaries, and boundless becoming. Triumphant living is a mindset that molds our manners and methods, shaping how we look, live, and lead.',
    paragraphs: [],
  },
  {
    slug: 'wednesdays-word-2026-06-17',
    title: "Wednesday's Word",
    excerpt:
      'A leader devoted to enlightenment cultivates clarity, curiosity, and lasting change, while one chasing entertainment delivers delight that quickly dissolves.',
    author: 'Troy Smith',
    publishedAt: '2026-06-17',
    format: 'daily',
    topic: "Wednesday's Word",
    readTimeMinutes: 2,
    quote:
      'A leader devoted to enlightenment cultivates clarity, curiosity, and lasting change, while one chasing entertainment delivers delight that quickly dissolves. True impact lives in the steady shaping of attitudes—not the momentary sparkle of attention.',
    paragraphs: [],
  },
  {
    slug: 'thoughtful-tuesday-2026-06-16',
    title: 'Thoughtful Tuesday',
    excerpt:
      'The thoughtful leader moves with intention, weighing each step to build lasting impact, while the impetuous leader rushes forward, often mistaking speed for strength.',
    author: 'Troy Smith',
    publishedAt: '2026-06-16',
    format: 'daily',
    topic: 'Thoughtful Tuesday',
    readTimeMinutes: 2,
    quote:
      'The thoughtful leader moves with intention, weighing each step to build lasting impact, while the impetuous leader rushes forward, often mistaking speed for strength. Enduring influence is forged through clarity and care, not haste and hubris.',
    paragraphs: [],
  },
  {
    slug: 'mindset-monday-2026-06-15',
    title: 'Mindset Monday',
    excerpt:
      "Resilience isn't revealed by retreating when life starts to bite; it's the mighty oak standing through savage storms, sinking its roots deeper with every relentless wind.",
    author: 'Troy Smith',
    publishedAt: '2026-06-15',
    format: 'daily',
    topic: 'Mindset Monday',
    readTimeMinutes: 2,
    quote:
      "Resilience isn't revealed by retreating when life starts to bite; it's the mighty oak standing through savage storms, sinking its roots deeper with every relentless wind. Strength is built through steadfastness, not shrinking.",
    paragraphs: [],
  },
  {
    slug: 'sovereignty-sunday-2026-06-14',
    title: 'Sovereignty Sunday',
    excerpt:
      'The sovereignty of God may not always make sense in the moment—it can feel like walking through fog without a clear path—but His track record stands like an unshakable mountain.',
    author: 'Troy Smith',
    publishedAt: '2026-06-14',
    format: 'daily',
    topic: 'Sovereignty Sunday',
    readTimeMinutes: 2,
    quote:
      'The sovereignty of God may not always make sense in the moment—it can feel like walking through fog without a clear path—but His track record stands like an unshakable mountain, marked by faithfulness that has never failed. What we question in the present is already covered by a history that proves He does not err, does not miss, and does not lose.',
    paragraphs: [],
  },
  {
    slug: 'shackle-free-saturday-2026-06-13',
    title: 'Shackle Free Saturday',
    excerpt:
      'A person does not accidentally stumble upon an emancipated life—it is the quiet discipline of introspectively looking within, choosing conviction over comfort.',
    author: 'Troy Smith',
    publishedAt: '2026-06-13',
    format: 'daily',
    topic: 'Shackle Free Saturday',
    readTimeMinutes: 2,
    quote:
      'A person does not accidentally stumble upon an emancipated life—it is the quiet discipline of introspectively looking within, choosing conviction over comfort, and freedom over familiar chains. The leader who lives this way becomes like a lion walking unshackled across the plains—self-possessed, intentional, and fully aware of its strength—so their impact doesn’t just assert itself, it resonates with a presence that commands and endures.',
    paragraphs: [],
  },
  {
    slug: 'focused-friday-2026-06-12',
    title: 'Focused Friday',
    excerpt:
      'A focused leader in the midst of conflict becomes like a steady drum in the chaos of battle—while everything around them rattles and roars, their rhythm never breaks.',
    author: 'Troy Smith',
    publishedAt: '2026-06-12',
    format: 'daily',
    topic: 'Focused Friday',
    readTimeMinutes: 2,
    quote:
      'A focused leader in the midst of conflict becomes like a steady drum in the chaos of battle—while everything around them rattles and roars, their rhythm never breaks. Those watching don’t just see strength, they feel it—because when conduct stays grounded and cadence stays true, their unwavering presence turns turmoil into trust and multiplies the weight of every move they make.',
    paragraphs: [],
  },
  {
    slug: 'triumphant-thursday-2026-06-11',
    title: 'Triumphant Thursday',
    excerpt:
      'Sometimes the very losses we resist are the quiet surgeons of our destiny—cutting away what drains us and clearing space for what restores us.',
    author: 'Troy Smith',
    publishedAt: '2026-06-11',
    format: 'daily',
    topic: 'Triumphant Thursday',
    readTimeMinutes: 2,
    quote:
      'Sometimes the very losses we resist are the quiet surgeons of our destiny—cutting away what drains us and clearing space for what restores us. When you release what never honored your worth, you break the tether that dulled your impact, and step into a strength that doesn’t just recover—it rises, multiplies, and triumphs beyond what you thought was lost.',
    paragraphs: [],
  },
  {
    slug: 'wednesdays-word-2026-06-10',
    title: "Wednesday's Word",
    excerpt:
      'A discerning leader moves like a gardener who knows which roots to nourish and which weeds to refuse—unmoved by the tempting bloom of quick, shallow wins.',
    author: 'Troy Smith',
    publishedAt: '2026-06-10',
    format: 'daily',
    topic: "Wednesday's Word",
    readTimeMinutes: 2,
    quote:
      'A discerning leader moves like a gardener who knows which roots to nourish and which weeds to refuse—unmoved by the tempting bloom of quick, shallow wins. While the impulsive rush to grab at every bright distraction and exhaust the soil, the wise leader cultivates with intention, turning restraint into lasting growth—so their impact doesn’t just appear, it flourishes and returns in abundance.',
    paragraphs: [],
  },
  {
    slug: 'transparency-tuesday-2026-06-09',
    title: 'Transparency Tuesday',
    excerpt:
      'Genuine leadership is a blazing inferno in a world of flickering sparks—while others chase borrowed light, you become the fire people gather around.',
    author: 'Troy Smith',
    publishedAt: '2026-06-09',
    format: 'daily',
    topic: 'Transparency Tuesday',
    readTimeMinutes: 2,
    quote:
      'Genuine leadership is a blazing inferno in a world of flickering sparks—while others chase borrowed light, you become the fire people gather around. When you lead as your true self, you don’t just guide the way—you ignite trust, spark courage, and turn ordinary moments into lasting impact.',
    paragraphs: [],
  },
  {
    slug: 'mindset-monday-2026-06-08',
    title: 'Mindset Monday',
    excerpt:
      'When you truly know your worth, you stop settling for substitutes. A diamond doesn\'t compete with common glass, and an eagle doesn\'t trade its skyward strength for a safe stroll on the ground.',
    author: 'Troy Smith',
    publishedAt: '2026-06-08',
    format: 'daily',
    topic: 'Mindset Monday',
    readTimeMinutes: 2,
    quote:
      'When you truly know your worth, you stop settling for substitutes. A diamond doesn\'t compete with common glass, and an eagle doesn\'t trade its skyward strength for a safe stroll on the ground. Your value is revealed by the standards you set and the counterfeits you refuse. Know your worth, protect your peace, and never exchange your destined treasure for temporary trinkets.',
    paragraphs: [],
  },
  {
    slug: 'sovereignty-sunday-steady-wind',
    title: 'Sovereignty Sunday',
    excerpt:
      'A leader who trusts God’s sovereignty is like a sail set into a steady wind—confident not in their own strength, but in the power guiding them forward.',
    author: 'Troy Smith',
    publishedAt: '2026-05-17',
    format: 'daily',
    topic: 'Sovereignty Sunday',
    featured: true,
    readTimeMinutes: 2,
    quote:
      'A leader who trusts God’s sovereignty is like a sail set into a steady wind—confident not in their own strength, but in the power guiding them forward.',
    meaning:
      'Trusting God’s sovereignty frees leaders from fear, control, and uncertainty. When leaders believe that God is ultimately directing the course, they lead with peace, courage, and humility. Like a sail responding to the wind, their trust allows them to move forward with purpose—and that calm confidence deeply influences and reassures the people they lead.',
    paragraphs: [],
  },
  {
    slug: 'sovereignty-sunday-lighthouse',
    title: 'Sovereignty Sunday',
    excerpt:
      'God’s sovereignty is the steadfast lighthouse on the shore of our lives, guiding us even when the waves rise higher than we can fathom.',
    author: 'Troy Smith',
    publishedAt: '2026-04-05',
    format: 'daily',
    topic: 'Sovereignty Sunday',
    readTimeMinutes: 2,
    quote:
      'God’s sovereignty is the steadfast and secure lighthouse on the shore of our lives, guiding us even when the waves rise higher than our feeble nautical understanding can fathom.',
    meaning:
      'God’s sovereign hand remains our unwavering source of direction and safety, even when life’s storms rise beyond what our limited knowledge can comprehend.',
    paragraphs: [],
  },
  {
    slug: 'sovereignty-sunday-kingdom-of-light',
    title: 'Sovereignty Sunday',
    excerpt:
      'God pulled you from the shadows, broke the old chains, and brought you into a kingdom glowing with light and hope.',
    author: 'Troy Smith',
    publishedAt: '2026-04-19',
    format: 'daily',
    topic: 'Sovereignty Sunday',
    readTimeMinutes: 2,
    quote:
      'God didn’t just guide you out—He pulled you from the shadows, broke the old chains, and brought you into a kingdom glowing with light and hope. You’re no longer a resident of darkness; you’re a citizen of His freedom and grace.',
    paragraphs: [],
  },
  {
    slug: 'shackle-free-saturday-liberated-leader',
    title: 'Shackle Free Saturday',
    excerpt:
      'A leader who lives liberated is like a bird soaring above the sky—unbound by fear or regret, showing others the freedom that comes from rising higher.',
    author: 'Troy Smith',
    publishedAt: '2026-05-09',
    format: 'daily',
    topic: 'Shackle Free Saturday',
    readTimeMinutes: 2,
    quote:
      'A leader who lives liberated is like a bird soaring above the sky—unbound by fear or regret, showing others the freedom that comes from rising higher.',
    meaning:
      'The essence of true leadership flows from inner freedom. When leaders are liberated from fear, insecurity, and past limitations, they lead with authenticity, courage, and clarity. Like a bird in flight, their freedom inspires others to believe they too can rise above constraints. A liberated life doesn’t just change the leader—it empowers everyone they touch to live and lead more boldly.',
    paragraphs: [],
  },
  {
    slug: 'shackle-free-saturday-emancipated-life',
    title: 'Shackle Free Saturday',
    excerpt:
      'To live an emancipated life is to rise each day with the strength to break every chain designed to keep you shackled.',
    author: 'Troy Smith',
    publishedAt: '2026-04-11',
    format: 'daily',
    topic: 'Shackle Free Saturday',
    readTimeMinutes: 2,
    quote:
      'To live an emancipated life is to rise each day with the strength to break every chain designed to keep you shackled, and walk boldly into the freedom you were created to claim.',
    paragraphs: [],
  },
  {
    slug: 'focused-friday-compass',
    title: 'Focused Friday',
    excerpt:
      'A focused leader is like a compass on an open map—steady, clear, and unwavering, helping everyone move forward without losing their way.',
    author: 'Troy Smith',
    publishedAt: '2026-05-15',
    format: 'daily',
    topic: 'Focused Friday',
    readTimeMinutes: 2,
    quote:
      'A focused leader is like a compass on an open map—steady, clear, and unwavering, helping everyone move forward without losing their way.',
    meaning:
      'Focus gives the leader direction and clarity. When a leader knows what truly matters and keeps their attention fixed on the mission, others feel confident following our lead. Like a compass, focus prevents distraction, aligns effort, and helps people move forward together with purpose. A focused leader doesn’t just make progress—they help everyone stay oriented toward meaningful impact.',
    paragraphs: [],
  },
  {
    slug: 'triumphant-thursday-mountain-sunrise',
    title: 'Triumphant Thursday',
    excerpt:
      'A triumphant leader is like a mountain sunrise—rising above the shadows and reminding others that a new day is always possible.',
    author: 'Troy Smith',
    publishedAt: '2026-05-14',
    format: 'daily',
    topic: 'Triumphant Thursday',
    readTimeMinutes: 2,
    quote:
      'A triumphant leader is like a mountain sunrise—rising above the shadows, warming everything below, and reminding others that a new day is always possible.',
    meaning:
      'Living triumphantly shapes how others see challenges, setbacks, and possibilities. When leaders consistently rise with resilience, hope, and confidence, they lift the perspective of everyone around them. Like a sunrise over the mountains, triumphant living doesn’t ignore darkness—it overcomes it, inspiring people to believe, persevere, and move forward with renewed strength.',
    paragraphs: [],
  },
  {
    slug: 'worry-free-wednesday-anchor',
    title: 'Worry Free Wednesday',
    excerpt:
      'A leader who refuses to worry is like an anchor in a storm—steady, grounded, and strong enough to keep everyone from drifting in fear.',
    author: 'Troy Smith',
    publishedAt: '2026-05-06',
    format: 'daily',
    topic: 'Worry Free Wednesday',
    readTimeMinutes: 2,
    quote:
      'A leader who refuses to worry is like an anchor in a storm—steady, grounded, and strong enough to keep everyone from drifting in the waves of fear.',
    meaning:
      'Leaders set the emotional tone for those they lead. When a leader is consumed by worry, uncertainty spreads; when a leader remains calm and grounded, confidence grows. Like an anchor during a storm, a non-worrier brings stability, reassurance, and direction—allowing people to focus, persevere, and move forward with trust rather than trepidation.',
    paragraphs: [],
  },
  {
    slug: 'worry-free-wednesday-prayer',
    title: 'Worry Free Wednesday',
    excerpt:
      'Worry is a conversation you have with yourself about things you cannot change. Prayer is a conversation you have with God about things He can change.',
    author: 'Troy Smith',
    publishedAt: '2026-04-08',
    format: 'daily',
    topic: 'Worry Free Wednesday',
    readTimeMinutes: 1,
    quote:
      'Worry is a conversation you have with yourself about things you cannot change. Prayer is a conversation you have with God about things He can change.',
    paragraphs: [],
  },
  {
    slug: 'transparency-tuesday-clean-glass',
    title: 'Transparent Tuesday',
    excerpt:
      'Transparency in leadership is like clean glass—when people can clearly see your heart and intentions, trust lets the light in.',
    author: 'Troy Smith',
    publishedAt: '2026-05-05',
    format: 'daily',
    topic: 'Transparent Tuesday',
    readTimeMinutes: 2,
    quote:
      'Transparency in leadership is like clean glass—when people can clearly see your heart and intentions, trust lets the light in, and everything grows.',
    meaning:
      'Transparency allows people to clearly understand a leader’s motives, values, and decisions. Like clean glass that lets sunlight nourish growth, openness builds trust and creates an environment where people feel safe, inspired, and empowered. When leaders are honest and visible, their influence deepens because people follow not just the position—but the person.',
    paragraphs: [],
  },
  {
    slug: 'motivational-monday-peace-and-purpose',
    title: 'Motivational Monday',
    excerpt:
      'Protecting your peace and pursuing your purpose are intrinsically interwoven.',
    author: 'Troy Smith',
    publishedAt: '2026-04-27',
    format: 'daily',
    topic: 'Motivational Monday',
    featured: true,
    readTimeMinutes: 2,
    quote: 'Protecting your peace and pursuing your purpose are intrinsically interwoven.',
    meaning:
      'Inner peace and purpose do not exist in silos from one another—they are connected to such an extent to where neglect of one is neglect of the other; honor of one is honor of the other.',
    paragraphs: [],
  },
  {
    slug: 'transparent-tuesday',
    title: 'Transparent Tuesday',
    excerpt:
      'Transparency is like turning on a light in a dark room — when we share our truth, it helps others find their way too.',
    author: 'Troy Smith',
    publishedAt: '2026-03-17',
    format: 'daily',
    topic: 'Transparent Tuesday',
    featured: true,
    readTimeMinutes: 2,
    quote:
      'Transparency is like turning on a light in a dark room —when we share our truth, it doesn’t just illuminate our own path, it helps others find their way too.',
    meaning:
      'When we choose to openly share our truth, it not only clarifies our own journey but also guides and encourages others who might be navigating similar darkness.',
    paragraphs: [],
  },
  {
    slug: 'building-trust-after-a-hard-season',
    title: 'Building Trust After a Hard Season',
    excerpt:
      'Trust is rebuilt in small, repeatable moments—not in one dramatic conversation. Here is a framework couples use to move from guarded to grounded.',
    author: 'Sharrea Smith',
    publishedAt: '2026-04-02',
    format: 'article',
    topic: 'Marriage',
    featured: true,
    readTimeMinutes: 6,
    paragraphs: [
      'After conflict or distance, couples often want a single breakthrough. In practice, trust returns when promises match behavior over time. That means naming what felt unsafe, asking what repair looks like for both people, and choosing micro-commitments you can keep this week.',
      'We coach couples to track “trust deposits” the way you might track steps: small, visible actions that say I am for you. When both partners contribute, the emotional climate shifts faster than either expects.',
    ],
  },
  {
    slug: 'the-leader-you-want-to-be-under-pressure',
    title: 'The Leader You Want to Be Under Pressure',
    excerpt:
      'Pressure reveals habits. Here is how to widen the gap between stimulus and response so your team experiences steadiness, not volatility.',
    author: 'Troy Smith',
    publishedAt: '2026-03-28',
    format: 'article',
    topic: 'Leadership',
    featured: true,
    readTimeMinutes: 7,
    paragraphs: [
      'Leadership is less about charisma in calm water and more about clarity when the water churns. Under stress, people do not need a perfect plan—they need a predictable leader. Predictability comes from naming reality, choosing one priority, and communicating it in plain language.',
      'Try a simple rhythm: pause for one breath before you answer in meetings, summarize what you heard before you decide, and close with who owns what by when. Small disciplines protect culture when everything else feels loud.',
    ],
  },
  {
    slug: 'listening-like-it-matters',
    title: 'Listening Like It Matters',
    excerpt:
      'Most “listening” is waiting to reply. Here are three cues that signal real presence—and how they change difficult conversations at home and work.',
    author: 'Sharrea Smith',
    publishedAt: '2026-03-22',
    format: 'article',
    topic: 'Communication',
    readTimeMinutes: 5,
    paragraphs: [
      'Presence begins with curiosity. Instead of fixing, try reflecting: “It sounds like you felt overlooked when that happened—is that right?” Reflection lowers defensiveness because the other person feels understood before solutions arrive.',
      'If you want a practical habit, try the two-minute rule: give two uninterrupted minutes while the other person speaks, then ask one clarifying question before you respond. You will be surprised how often the conversation shortens.',
    ],
  },
  {
    slug: 'money-talks-without-the-spiral',
    title: 'Money Talks Without the Spiral',
    excerpt:
      'Financial stress is rarely only about numbers. A simple agenda can keep couples aligned when budgets feel tight or dreams feel far away.',
    author: 'Troy Smith',
    publishedAt: '2026-03-18',
    format: 'article',
    topic: 'Financial wellness',
    readTimeMinutes: 6,
    paragraphs: [
      'Couples often argue about spending when they are actually negotiating safety and freedom. Start by naming the emotion under the spreadsheet: fear of falling behind, shame about past choices, or longing for margin.',
      'Then keep the meeting short: one shared win, one tension to solve, one next step. Money meetings work best when they are frequent, kind, and boring.',
    ],
  },
  {
    slug: 'parenting-as-a-united-front',
    title: 'Parenting as a United Front',
    excerpt:
      'Kids feel inconsistency quickly. Here is how co-parents align values without forcing identical styles.',
    author: 'Sharrea Smith',
    publishedAt: '2026-03-12',
    format: 'article',
    topic: 'Parenting',
    readTimeMinutes: 5,
    paragraphs: [
      'United does not mean identical. It means children experience clarity: these are the family rules, and both adults support them in public even when they negotiate differences in private.',
      'A weekly fifteen-minute “parent huddle” can prevent drift. Agree on the top behavior you are shaping this month, the consequence if boundaries are crossed, and how you will encourage progress.',
    ],
  },
  {
    slug: 'purpose-when-the-plan-changes',
    title: 'Purpose When the Plan Changes',
    excerpt:
      'Transitions—job loss, relocation, illness—can hollow out identity. Faith and reflection help you rebuild meaning without pretending pain is small.',
    author: 'Troy Smith',
    publishedAt: '2026-03-08',
    format: 'article',
    topic: 'Faith & purpose',
    readTimeMinutes: 6,
    paragraphs: [
      'Purpose is not only a destination; it is faithfulness in the next faithful step. When the plan you loved disappears, grief is honest. Naming loss keeps you from rushing into fake positivity.',
      'Many people find traction by asking three questions: What can I learn here? Who can I love well in this season? What is one small practice that keeps me anchored?',
    ],
  },
  {
    slug: 'retreat-weekend-what-to-expect',
    title: 'Retreat Weekend: What to Expect',
    excerpt:
      'If you are considering an I-3 retreat, here is a gentle overview of rhythm, privacy, and how couples typically experience the arc of the weekend.',
    author: 'Sharrea Smith',
    publishedAt: '2026-03-01',
    format: 'article',
    topic: 'Retreats & events',
    readTimeMinutes: 4,
    paragraphs: [
      'Retreats are designed to be spacious, not rushed. You will encounter teaching, guided conversation, and unscheduled time to breathe. Nothing is designed to embarrass you; participation is always invitational.',
      'Most couples leave with a shared language for what felt stuck and a few concrete practices to try at home. Bring honesty and a willingness to pause—those two things do more than perfect answers.',
    ],
  },
  {
    slug: 'boundaries-that-protect-love',
    title: 'Boundaries That Protect Love',
    excerpt:
      'Boundaries are not walls against your partner; they are guardrails for trust. Here is how to say no with warmth.',
    author: 'Troy Smith',
    publishedAt: '2026-02-24',
    format: 'article',
    topic: 'Boundaries',
    readTimeMinutes: 5,
    paragraphs: [
      'Healthy boundaries sound like: “I want to be with you, and I need this to be different.” The goal is clarity, not control. When boundaries are clear, resentment has less room to grow.',
      'Practice naming the need behind the boundary: rest, respect, safety, or pace. Needs are easier to honor than vague complaints.',
    ],
  },
  {
    slug: 'leading-through-uncertainty',
    title: 'Leading Through Uncertainty',
    excerpt:
      'Your team is watching how you speak about what you do not know. Here is a simple communication pattern for ambiguous seasons.',
    author: 'Sharrea Smith',
    publishedAt: '2026-02-18',
    format: 'article',
    topic: 'Leadership',
    readTimeMinutes: 6,
    paragraphs: [
      'Uncertainty is not the enemy; unmanaged ambiguity is. Leaders help by separating what is decided, what is being decided, and what cannot be known yet. That framing reduces rumor-spirals.',
      'Share the decision timeline—even if the answer is “we will revisit Friday.” Predictable process creates psychological safety when outcomes are unclear.',
    ],
  },
  {
    slug: 'repair-after-a-harsh-word',
    title: 'Repair After a Harsh Word',
    excerpt:
      'Repair is a skill. This short guide walks through owning impact, not just intent, and rebuilding connection after a sharp moment.',
    author: 'Troy Smith',
    publishedAt: '2026-02-12',
    format: 'article',
    topic: 'Marriage',
    readTimeMinutes: 5,
    paragraphs: [
      'Repair begins with impact: “What I said landed in a hurtful way, and I am sorry.” Intent can be true and incomplete. People forgive faster when they feel understood.',
      'Then ask what would help tonight: space, a hug, a redo conversation, or a plan to revisit tomorrow when you are both rested.',
    ],
  },
  {
    slug: 'emotional-safety-at-work',
    title: 'Emotional Safety at Work',
    excerpt:
      'Teams perform better when disagreement is possible without punishment. Leaders set the tone with curiosity and consistency.',
    author: 'Sharrea Smith',
    publishedAt: '2026-02-05',
    format: 'article',
    topic: 'Team culture',
    readTimeMinutes: 6,
    paragraphs: [
      'Safety does not mean comfort; it means risk is survivable. That requires norms: we debate ideas without attacking people, we speak directly with kindness, and we follow up after hard meetings.',
      'If you lead, model naming tension early. Small corrections prevent large crises.',
    ],
  },
  {
    slug: 'when-you-feel-alone-in-the-house',
    title: 'When You Feel Alone in the House',
    excerpt:
      'Loneliness inside a relationship is surprisingly common. Here are gentle steps toward reconnection without blame.',
    author: 'Troy Smith',
    publishedAt: '2026-01-30',
    format: 'article',
    topic: 'Marriage',
    readTimeMinutes: 5,
    paragraphs: [
      'Loneliness often signals longing—for play, for partnership, for being known. Start by describing an emotion rather than diagnosing character: “I miss us” lands differently than “You never…”',
      'Rebuild connection with small rituals: a ten-minute evening check-in, a weekly walk, or one screen-free meal. Consistency matters more than intensity.',
    ],
  },
  {
    slug: 'faith-and-mental-health-together',
    title: 'Faith and Mental Health Together',
    excerpt:
      'Faith and therapy are not competitors. Here is language that honors both when you are supporting someone you love.',
    author: 'Sharrea Smith',
    publishedAt: '2026-01-22',
    format: 'article',
    topic: 'Faith & purpose',
    readTimeMinutes: 6,
    paragraphs: [
      'Faith offers hope and community; clinical care offers tools and assessment. Many people need both. If you are unsure where to start, a trusted professional can help you understand symptoms and options.',
      'If you are supporting a friend, avoid quick fixes. Presence often sounds like: “I am with you. What would help today?”',
    ],
  },
  {
    slug: 'the-art-of-the-difficult-email',
    title: 'The Art of the Difficult Email',
    excerpt:
      'Written words linger. A simple template helps you be clear, kind, and unambiguous when stakes are high.',
    author: 'Troy Smith',
    publishedAt: '2026-01-15',
    format: 'article',
    topic: 'Communication',
    readTimeMinutes: 4,
    paragraphs: [
      'Start with context in one sentence, then facts, then the request. Avoid sarcasm and stacked adjectives—tone is easy to misread.',
      'If the conversation is relational, consider a call first. Email is best for alignment and documentation, not emotional processing.',
    ],
  },
  {
    slug: 'raising-teens-with-firm-kindness',
    title: 'Raising Teens with Firm Kindness',
    excerpt:
      'Adolescence is a season of testing. Firm boundaries paired with warmth help teens feel both safe and respected.',
    author: 'Sharrea Smith',
    publishedAt: '2026-01-08',
    format: 'article',
    topic: 'Parenting',
    readTimeMinutes: 5,
    paragraphs: [
      'Teens need autonomy and limits in the same week—sometimes the same day. Explain the why behind rules when you can; invite their input on how rules are lived out when you can.',
      'Repair matters here too. When you lose your temper, model apology. It teaches dignity.',
    ],
  },
  {
    slug: 'your-calendar-is-a-moral-document',
    title: 'Your Calendar Is a Moral Document',
    excerpt:
      'Time is not neutral. What your week rewards will grow—here is how to align your schedule with your stated priorities.',
    author: 'Troy Smith',
    publishedAt: '2025-12-18',
    format: 'article',
    topic: 'Leadership',
    readTimeMinutes: 5,
    paragraphs: [
      'Leaders often say family matters while the calendar says otherwise. Start with one protected block each week that cannot be moved without a serious reason.',
      'If your values and calendar conflict, adjust the calendar first. Small changes beat dramatic speeches.',
    ],
  },
  {
    slug: 'holidays-without-the-scoreboard',
    title: 'Holidays Without the Scoreboard',
    excerpt:
      'Extended family gatherings can become performance reviews. Here is how couples prepare expectations and exit ramps.',
    author: 'Sharrea Smith',
    publishedAt: '2025-12-10',
    format: 'article',
    topic: 'Marriage',
    readTimeMinutes: 5,
    paragraphs: [
      'Decide in advance what success looks like: connection, peace, or simply surviving with kindness. Share a signal if you need a break—a word, a look, a short walk.',
      'Remember: you are allowed to disappoint people’s expectations if it protects your household’s health.',
    ],
  },
  {
    slug: 'writing-a-personal-rule-of-life',
    title: 'Writing a Personal Rule of Life',
    excerpt:
      'A rule of life is not legalism—it is rhythm. A simple template helps you name practices that keep you grounded.',
    author: 'Troy Smith',
    publishedAt: '2025-12-02',
    format: 'article',
    topic: 'Faith & purpose',
    readTimeMinutes: 6,
    paragraphs: [
      'Try four buckets: spiritual, relational, physical, and work. Under each, choose one habit you can keep for thirty days. Small fidelity forms identity.',
      'Review monthly. Grace is built into the practice; perfection is not the point.',
    ],
  },
  {
    slug: 'after-the-conference-high',
    title: 'After the Conference High',
    excerpt:
      'Events can inspire—and then Monday arrives. Here is how teams turn inspiration into one meaningful change.',
    author: 'Sharrea Smith',
    publishedAt: '2025-11-20',
    format: 'article',
    topic: 'Retreats & events',
    readTimeMinutes: 4,
    paragraphs: [
      'Capture one insight and one action before you leave the parking lot. Inspiration fades when it stays abstract.',
      'If you attended as a couple or team, schedule a thirty-minute debrief within a week. One shared commitment beats ten forgotten bullet points.',
    ],
  },
  {
    slug: 'rest-as-a-leadership-strategy',
    title: 'Rest as a Leadership Strategy',
    excerpt:
      'Exhausted leaders make fearful decisions. Rest is not laziness; it is stewardship of clarity.',
    author: 'Troy Smith',
    publishedAt: '2025-11-12',
    format: 'article',
    topic: 'Emotional health',
    readTimeMinutes: 5,
    paragraphs: [
      'If you would not want your team to live at your pace, change the pace. Rest includes sleep, yes, but also boundaries on availability and realistic workloads.',
      'Model rest publicly: protect a day off, mention it, and honor it. Culture follows what is rewarded.',
    ],
  },
  {
    slug: 'before-you-say-i-do-conversations',
    title: 'Before You Say I Do: Conversations Worth Having',
    excerpt:
      'Engagement is exciting—and preparation is kindness. Here are topics that help couples enter marriage with fewer surprises.',
    author: 'Sharrea Smith',
    publishedAt: '2025-10-28',
    format: 'article',
    topic: 'Marriage',
    readTimeMinutes: 6,
    paragraphs: [
      'Money, family boundaries, faith, and conflict style are not “bonus” topics. They are part of the foundation. You do not need every answer; you need honesty and a plan for how you will decide together.',
      'Premarital coaching is not a test you pass—it is a space to align expectations and build habits you will actually use in year one and year twenty.',
    ],
  },
  {
    slug: 'stewardship-without-shame',
    title: 'Stewardship Without Shame',
    excerpt:
      'Faith and finances both ask for honesty. Here is how to talk about giving, debt, and goals without turning the table into a courtroom.',
    author: 'Troy Smith',
    publishedAt: '2025-10-15',
    format: 'article',
    topic: 'Financial wellness',
    readTimeMinutes: 5,
    paragraphs: [
      'Shame hides numbers; clarity invites teamwork. Start with gratitude and reality: what do we have, what do we owe, and what are we trying to fund in this season?',
      'Stewardship is not about perfection—it is about faithful next steps and mutual respect when tradeoffs appear.',
    ],
  },
];

export function withResolvedBlogFormat(post: I3BlogPost): I3BlogPost {
  return { ...post, format: resolveBlogFormat(post) };
}

export function getI3BlogPostsSorted(): I3BlogPost[] {
  return I3_BLOG_POSTS.map(withResolvedBlogFormat).sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );
}

export function getI3BlogPostBySlug(slug: string | undefined): I3BlogPost | undefined {
  if (!slug) return undefined;
  const post = I3_BLOG_POSTS.find((p) => p.slug === slug);
  return post ? withResolvedBlogFormat(post) : undefined;
}
